import { useEffect, useMemo } from 'react';
import { Formik, Form, useFormikContext } from 'formik';
import { object, string, array, boolean, number, SchemaOf } from 'yup';
import { ArrowLeftRight, CheckCircle, AlertCircle } from 'lucide-react';

import { Widget, WidgetBody } from '@@/Widget';
import { FormSectionTitle } from '@@/form-components/FormSectionTitle';
import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';
import { Switch } from '@@/form-components/SwitchField/Switch';
import { LoadingButton } from '@@/buttons';
import { TextTip } from '@@/Tip/TextTip';

import { TypedPolicy } from '../../../types';
import { useNameValidation } from '../../../validation/useNameValidation';
import { PolicyConfigurationFormSection } from '../../PolicyConfigurationFormSection';
import { PolicyFormFooter } from '../../PolicyFormFooter';
import { useTestObservabilityConnectionMutation } from '../../../queries/useTestObservabilityConnectionMutation';

import {
  ObservabilityK8sFormValues,
  ObservabilityPreset,
  OBSERVABILITY_PRESETS,
} from './types';
import { useKubernetesObservabilityInitialValues } from './useKubernetesObservabilityInitialValues';

function ObservabilityTestConnection({
  url,
  apiKey,
  projectID,
  tlsSkipVerify,
  policyId,
  autoRotateKey,
  onTestSuccess,
}: {
  url: string;
  apiKey: string;
  projectID: string;
  tlsSkipVerify: boolean;
  policyId?: number;
  autoRotateKey: boolean;
  onTestSuccess?: () => void;
}) {
  const testMutation = useTestObservabilityConnectionMutation();

  const disabled = !url || (!apiKey && !policyId) || !projectID;
  const testResult = testMutation.data ?? null;

  return (
    <div className="flex flex-row items-center gap-3">
      <LoadingButton
        size="small"
        color="default"
        className="!ml-0 w-min"
        isLoading={testMutation.isLoading}
        icon={ArrowLeftRight}
        loadingText="Testing connection..."
        onClick={handleTest}
        disabled={disabled}
        type="button"
        data-cy="observability-test-connection-button"
      >
        Test connection and permission
      </LoadingButton>

      {testResult && (
        <TextTip
          className="!items-start [&>svg]:mt-0.5"
          icon={testResult.success ? CheckCircle : AlertCircle}
          color={testResult.success ? 'green' : 'red'}
        >
          {testResult.message ??
            (testResult.success
              ? 'Connection successful.'
              : 'Connection failed.')}
        </TextTip>
      )}
    </div>
  );

  function handleTest() {
    testMutation.mutate(
      {
        oneUptimeURL: url,
        apiKey,
        projectID,
        tlsSkipVerify,
        policyId,
        autoRotateKey,
      },
      {
        onSuccess: (data) => {
          if (data.success) onTestSuccess?.();
        },
      }
    );
  }
}

// Resets connectionVerified whenever an input that changes what the test validates changes.
// This prevents submitting with auto-rotation enabled after swapping credentials without re-testing.
function ConnectionVerifiedReset() {
  const { values, setFieldValue } =
    useFormikContext<ObservabilityK8sFormValues>();

  useEffect(() => {
    setFieldValue('connectionVerified', false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.oneUptimeURL,
    values.apiKey,
    values.projectID,
    values.autoRotateKey,
  ]);

  return null;
}

interface Props {
  onSubmit: (values: ObservabilityK8sFormValues) => Promise<void>;
  mode: 'create' | 'edit';
  policy?: TypedPolicy;
  templateData?: Record<string, unknown>;
  onBack?: () => void;
  canGoBack?: boolean;
}

export function ObservabilityK8sPolicyForm({
  onSubmit,
  mode,
  policy,
  templateData,
  onBack,
  canGoBack,
}: Props) {
  const initialValues = useKubernetesObservabilityInitialValues(
    policy,
    templateData
  );
  const originalEnvironmentGroupIds = policy?.EnvironmentGroups || [];
  const nameValidation = useNameValidation(policy?.Id);
  const validationSchema = useMemo(
    () => createValidationSchema(nameValidation, mode),
    [nameValidation, mode]
  );

  return (
    <Formik<ObservabilityK8sFormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      enableReinitialize={mode === 'edit'}
      validateOnMount
    >
      <Form className="form-horizontal">
        <Widget>
          <WidgetBody>
            {mode === 'create' && (
              <PolicyConfigurationFormSection policyType="observability-k8s" />
            )}
            <ObservabilityFormFields mode={mode} policyId={policy?.Id} />
            {mode === 'edit' && (
              <PolicyFormFooter
                mode={mode}
                policyType="observability-k8s"
                policyId={policy?.Id ?? null}
                originalEnvironmentGroupIds={originalEnvironmentGroupIds}
              />
            )}
          </WidgetBody>
        </Widget>
        {mode === 'create' && (
          <PolicyFormFooter
            mode={mode}
            policyType="observability-k8s"
            policyId={policy?.Id ?? null}
            onBack={onBack}
            canGoBack={canGoBack}
          />
        )}
      </Form>
    </Formik>
  );
}

function ObservabilityFormFields({
  mode,
  policyId,
}: {
  mode: 'create' | 'edit';
  policyId?: number;
}) {
  const { values, setFieldValue, errors, touched } =
    useFormikContext<ObservabilityK8sFormValues>();

  const isDeployMode = values.mode === 'deploy-and-connect';
  const isHttps = values.oneUptimeURL.startsWith('https://');

  return (
    <>
      <FormControl
        label="Description"
        errors={touched.description ? errors.description : undefined}
        size="large"
      >
        <Input
          type="text"
          value={values.description}
          onChange={(e) => setFieldValue('description', e.target.value)}
          placeholder="Optional description"
          data-cy="observability-description"
        />
      </FormControl>

      <FormSectionTitle>OneUptime Connection</FormSectionTitle>

      <FormControl
        label="OneUptime URL"
        required
        errors={touched.oneUptimeURL ? errors.oneUptimeURL : undefined}
        size="large"
      >
        <Input
          type="text"
          value={values.oneUptimeURL}
          onChange={(e) => {
            const newURL = e.target.value;
            setFieldValue('oneUptimeURL', newURL);
            if (!newURL.startsWith('https://')) {
              setFieldValue('tlsSkipVerify', false);
            }
          }}
          placeholder="https://oneuptime.com"
          data-cy="observability-oneuptime-url"
        />
      </FormControl>

      <FormControl
        label="Skip TLS verification"
        tooltip="Disable certificate verification for self-signed certificates. Only applicable for HTTPS URLs."
        size="large"
      >
        <Switch
          id="tls-skip-verify"
          name="tlsSkipVerify"
          checked={values.tlsSkipVerify && isHttps}
          disabled={!isHttps}
          onChange={(value) => setFieldValue('tlsSkipVerify', value)}
          data-cy="observability-tls-skip-verify"
        />
      </FormControl>

      <FormControl
        label="Project ID"
        required
        errors={touched.projectID ? errors.projectID : undefined}
        size="large"
      >
        <Input
          type="text"
          value={values.projectID}
          onChange={(e) => setFieldValue('projectID', e.target.value)}
          placeholder="Project ID"
          data-cy="observability-project-id"
        />
      </FormControl>

      <FormControl
        label="API key"
        required={mode === 'create'}
        errors={touched.apiKey ? errors.apiKey : undefined}
        size="large"
      >
        <Input
          type="password"
          autoComplete="new-password"
          value={values.apiKey}
          onChange={(e) => setFieldValue('apiKey', e.target.value)}
          placeholder={
            mode === 'create'
              ? 'Enter API key'
              : 'Leave empty to preserve stored value'
          }
          data-cy="observability-api-key"
        />
      </FormControl>

      <FormControl
        label="Allow automatic key rotation"
        tooltip="When enabled, Portainer will automatically rotate the OneUptime API key before it expires. The current key must have rotation permissions — verify this using the test button below before saving."
        size="large"
      >
        <Switch
          id="auto-rotate-key"
          name="autoRotateKey"
          checked={values.autoRotateKey}
          onChange={(value) => setFieldValue('autoRotateKey', value)}
          data-cy="observability-auto-rotate-key"
        />
      </FormControl>

      <ConnectionVerifiedReset />

      <div className="form-group">
        <div className="col-sm-12">
          <ObservabilityTestConnection
            url={values.oneUptimeURL}
            apiKey={values.apiKey}
            projectID={values.projectID}
            tlsSkipVerify={values.tlsSkipVerify && isHttps}
            policyId={policyId}
            autoRotateKey={values.autoRotateKey}
            onTestSuccess={() => setFieldValue('connectionVerified', true)}
          />
          {values.autoRotateKey && !values.connectionVerified && (
            <TextTip color="orange" className="mt-2">
              Test connection and permission must pass before saving with
              automatic key rotation enabled.
            </TextTip>
          )}
        </div>
      </div>

      <FormSectionTitle>Mode</FormSectionTitle>

      <div className="form-group">
        <div className="col-sm-12">
          <div className="flex gap-4">
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name="mode"
                value="connect-only"
                checked={values.mode === 'connect-only'}
                onChange={() => setFieldValue('mode', 'connect-only')}
                data-cy="observability-mode-connect-only"
              />
              Connect to existing agents
            </label>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="radio"
                name="mode"
                value="deploy-and-connect"
                checked={values.mode === 'deploy-and-connect'}
                onChange={() => setFieldValue('mode', 'deploy-and-connect')}
                data-cy="observability-mode-deploy-and-connect"
              />
              Deploy agents and connect
            </label>
          </div>
        </div>
      </div>

      {isDeployMode && (
        <>
          <FormSectionTitle>Agent Deployment</FormSectionTitle>

          <FormControl
            label="Ingestion Key"
            required
            tooltip="Ingestion Key from OneUptime (Settings > Ingestion Keys). Used by the agent to send telemetry data."
            errors={
              touched.deploy?.ingestionKey
                ? errors.deploy?.ingestionKey
                : undefined
            }
            size="large"
          >
            <Input
              type="password"
              value={values.deploy.ingestionKey}
              onChange={(e) =>
                setFieldValue('deploy.ingestionKey', e.target.value)
              }
              placeholder="Enter ingestion key"
              data-cy="observability-ingestion-key"
            />
          </FormControl>

          <FormControl
            label="Target namespace"
            required
            errors={
              touched.deploy?.targetNamespace
                ? errors.deploy?.targetNamespace
                : undefined
            }
            size="large"
          >
            <Input
              type="text"
              value={values.deploy.targetNamespace}
              onChange={(e) =>
                setFieldValue('deploy.targetNamespace', e.target.value)
              }
              placeholder="oneuptime"
              data-cy="observability-target-namespace"
            />
          </FormControl>

          <FormControl
            label="Helm release name"
            required
            errors={
              touched.deploy?.helmReleaseName
                ? errors.deploy?.helmReleaseName
                : undefined
            }
            size="large"
          >
            <Input
              type="text"
              value={values.deploy.helmReleaseName}
              onChange={(e) =>
                setFieldValue('deploy.helmReleaseName', e.target.value)
              }
              placeholder="oneuptime"
              data-cy="observability-helm-release-name"
            />
          </FormControl>

          <FormSectionTitle>Cluster Distribution</FormSectionTitle>

          <div className="form-group">
            <div className="col-sm-12">
              <div className="flex flex-col gap-2">
                {OBSERVABILITY_PRESETS.map(({ value, label, description }) => (
                  <label
                    key={value}
                    className="flex cursor-pointer items-center gap-2"
                  >
                    <input
                      type="radio"
                      name="deploy.preset"
                      value={value}
                      checked={values.deploy.preset === value}
                      onChange={() => setFieldValue('deploy.preset', value)}
                      data-cy={`observability-preset-${value}`}
                    />
                    <span>
                      <strong>{label}</strong> — {description}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {!isDeployMode && (
        <>
          <FormSectionTitle>Existing Agent Validation</FormSectionTitle>

          <FormControl
            label="Expected namespace"
            errors={
              touched.detection?.expectedNamespace
                ? errors.detection?.expectedNamespace
                : undefined
            }
            size="large"
          >
            <Input
              type="text"
              value={values.detection.expectedNamespace}
              onChange={(e) =>
                setFieldValue('detection.expectedNamespace', e.target.value)
              }
              placeholder="oneuptime-agent"
              data-cy="observability-expected-namespace"
            />
          </FormControl>

          <FormControl
            label="Expected Helm release name"
            errors={
              touched.detection?.helmReleaseName
                ? errors.detection?.helmReleaseName
                : undefined
            }
            size="large"
          >
            <Input
              type="text"
              value={values.detection.helmReleaseName}
              onChange={(e) =>
                setFieldValue('detection.helmReleaseName', e.target.value)
              }
              placeholder="kubernetes-agent"
              data-cy="observability-detection-helm-release-name"
            />
          </FormControl>

          <FormControl
            label="Cluster name override"
            tooltip="Overrides the cluster name used to look up this environment in OneUptime. Leave empty to use the Portainer environment name."
            errors={
              touched.detection?.clusterName
                ? errors.detection?.clusterName
                : undefined
            }
            size="large"
          >
            <Input
              type="text"
              value={values.detection.clusterName}
              onChange={(e) =>
                setFieldValue('detection.clusterName', e.target.value)
              }
              placeholder="Leave empty to use environment name"
              data-cy="observability-detection-cluster-name"
            />
          </FormControl>
        </>
      )}
    </>
  );
}

function createValidationSchema(
  nameValidation: ReturnType<typeof useNameValidation>,
  mode: 'create' | 'edit'
): SchemaOf<ObservabilityK8sFormValues> {
  const deploySchema = object({
    ingestionKey: string().default(''),
    targetNamespace: string().default(''),
    helmReleaseName: string().default(''),
    preset: string()
      .oneOf(['standard', 'gke-autopilot', 'eks-fargate'] as const)
      .default('standard') as SchemaOf<ObservabilityPreset>,
  });

  const detectionSchema = object({
    expectedNamespace: string().default(''),
    helmReleaseName: string().default(''),
    clusterName: string().default(''),
  });

  return object({
    name: nameValidation,
    description: string().default(''),
    environmentGroupIds:
      mode === 'create'
        ? array(number().required())
            .min(1, 'At least one environment group is required')
            .default([])
        : array(number().required()).default([]),
    oneUptimeURL: string()
      .required('OneUptime URL is required')
      .url('Must be a valid URL'),
    apiKey:
      mode === 'create'
        ? string().required('API Key is required').default('')
        : string().default(''),
    projectID: string().required('Project ID is required'),
    mode: string()
      .oneOf(['connect-only', 'deploy-and-connect'] as const)
      .required() as SchemaOf<ObservabilityK8sFormValues['mode']>,
    deploy: deploySchema.when('mode', {
      is: 'deploy-and-connect',
      then: (s) =>
        s.shape({
          ingestionKey: string()
            .default('')
            .required('Ingestion key is required'),
          targetNamespace: string()
            .default('')
            .required('Target namespace is required'),
          helmReleaseName: string()
            .default('')
            .required('Helm release name is required'),
          preset: string()
            .oneOf(['standard', 'gke-autopilot', 'eks-fargate'] as const)
            .default('standard') as SchemaOf<ObservabilityPreset>,
        }),
    }) as SchemaOf<ObservabilityK8sFormValues['deploy']>,
    detection: detectionSchema,
    uiEnrichmentEnabled: boolean().required().default(false),
    tlsSkipVerify: boolean().required().default(false),
    autoRotateKey: boolean().required().default(false),
    connectionVerified: boolean()
      .required()
      .default(false)
      .when('autoRotateKey', {
        is: true,
        then: (s) =>
          s.isTrue(
            'Test connection and permission must pass before saving with automatic key rotation enabled.'
          ),
      }),
  }) as SchemaOf<ObservabilityK8sFormValues>;
}
